import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { useAuth } from "@/contexts/AuthContext";
import { useFreelancerDisciplines, useFreelancers } from "@/hooks/use-freelancers";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useFreelancerLastUsedHistory, useFreelancerProjectsEnabled } from "@/hooks/use-freelancer-projects";
import { lastUsedByFreelancer } from "@/lib/freelancer-projects";
import { DEFAULT_FREELANCER_FILTERS, filterFreelancers, type FreelancerFilters } from "@/lib/freelancers";
import { FreelancerListToolbar } from "@/components/freelancers/FreelancerListToolbar";
import { FreelancerListTable, type FreelancerSort } from "@/components/freelancers/FreelancerListTable";
import { FreelancerFormDialog } from "@/components/freelancers/FreelancerFormDialog";
import { DisciplineAdminDialog } from "@/components/freelancers/DisciplineAdminDialog";

/**
 * /frilansere (VIB-2925). Gruppert på hovedfagfelt til brukeren sorterer på en
 * kolonne. Filtrering skjer i klienten; banken er liten (rundt hundre rader).
 */
export default function FreelancersPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { data: baseRows = [], isLoading, error } = useFreelancers();
  const projectsOn = useFreelancerProjectsEnabled();
  const { data: history = [] } = useFreelancerLastUsedHistory(projectsOn);
  const rows = useMemo(() => {
    if (!projectsOn) return baseRows;
    const lastUsed = lastUsedByFreelancer(history);
    return baseRows.map((r) => ({ ...r, last_used: lastUsed.get(r.id) ?? null }));
  }, [baseRows, history, projectsOn]);
  const { data: disciplines = [] } = useFreelancerDisciplines();
  const [filters, setFilters] = useState<FreelancerFilters>(DEFAULT_FREELANCER_FILTERS);
  const [sort, setSort] = useState<FreelancerSort | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [disciplinesOpen, setDisciplinesOpen] = useState(false);
  const enabled = useFeatureFlag("feature_freelancer_bank");

  const visible = useMemo(() => filterFreelancers(rows, filters), [rows, filters]);
  const disciplineCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) for (const d of r.disciplines) m.set(d.id, (m.get(d.id) ?? 0) + 1);
    return m;
  }, [rows]);
  const activeCount = rows.filter((r) => !r.archived_at).length;

  if (!enabled) return <div className="page-container"><p className="text-muted-foreground">Ikke tilgjengelig ennå.</p></div>;

  return (
    <div className="page-container animate-fade-in">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 font-display text-[clamp(28px,3vw,40px)] font-semibold tracking-[-0.01em]">
          <SrIcon name="film" className="h-6 w-6" />
          Frilansere
        </h1>
        <div className="flex items-center gap-2">
          {projectsOn && (
            <Button variant="outline" onClick={() => navigate("/frilansere/leverandorer")}>Leverandører</Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={() => setDisciplinesOpen(true)}>Fagfelt</Button>
          )}
          <Button onClick={() => { setFormKey((k) => k + 1); setFormOpen(true); }} aria-label="Ny frilanser">
            <SrIcon name="plus" className="h-4 w-4" />
            Ny<span className="hidden sm:inline"> frilanser</span>
          </Button>
        </div>
      </div>

      {error ? (
        <p className="text-destructive">Feil ved lasting av frilansere: {(error as Error).message}</p>
      ) : (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <FreelancerListToolbar filters={filters} onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))} disciplines={disciplines} />
            {isLoading ? (
              <div className="flex justify-center py-8">
                <SrIcon name="loader2" className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <FreelancerListTable
                  rows={visible}
                  sort={sort}
                  onSortChange={setSort}
                  showLastUsed={projectsOn}
                  emptyMessage={rows.length === 0 ? "Ingen frilansere ennå. Legg til den første med «Ny frilanser»." : "Ingen frilansere matcher filtrene."}
                />
                <p className="text-xs text-muted-foreground">
                  Viser {visible.length} av {activeCount}. Priser er veiledende dagspriser, normalt eks. mva.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <FreelancerFormDialog key={formKey} open={formOpen} onOpenChange={setFormOpen} onSaved={(id) => navigate(`/frilansere/${id}`)} />
      {isAdmin && <DisciplineAdminDialog open={disciplinesOpen} onOpenChange={setDisciplinesOpen} counts={disciplineCounts} />}
    </div>
  );
}
